import { debounce, sleep, stopDebounce } from "@/src/utils/utils";
import { useState } from "react";
import { Button } from "./buttons";

export type DragDataType = {
  id: string;
  sourceListId: string;
  draggedIndex: number;
  item: Record<any, any>;
  sourceListType: string;
};

let removeFunctions: Record<string, (item: any, index: number) => void> = {};

let dragData: DragDataType | null = null;

function ReorderableItems({
  listId,
  listType,
  items,
  isHorizontal,
  isDisabled,
  onRemove,
  onReorder,
  onAddClick,
  addButtonTitle,
  children,
}: {
  listId: string;
  listType: any;
  items: any[];
  isDisabled?: boolean;
  isHorizontal?: boolean;
  onRemove?: (item: any, index: number) => void;
  onReorder: (
    newItems: any[],
    oldIndex: number,
    newIndex: number,
    movedItem: any,
    sourceListId: string
  ) => void;
  onAddClick?: () => void;
  addButtonTitle?: string;
  children?: any[];
}) {
  const [reorderedIndex, setReorderedIndex] = useState<number | null>(null);

  async function onDrop(isAfter: boolean, index: number) {
    if (!dragData) return;
    let { draggedIndex, item, sourceListId, sourceListType } = dragData;
    if (sourceListType != listType) return;
    if (draggedIndex === null) return;

    let newIndex = isAfter ? index + 1 : index;
    const newItems = [...items];
    if (
      draggedIndex !== null &&
      draggedIndex !== undefined &&
      sourceListId == listId
    )
      newItems.splice(draggedIndex, 1);
    newItems.splice(newIndex, 0, item);
    setReorderedIndex(newIndex);
    await onReorder(newItems, draggedIndex, newIndex, item, sourceListId);
    if (sourceListId != listId) {
      await sleep(50);
      removeFunctions[sourceListId]?.(item, draggedIndex);
    }
    await sleep(100);
    setReorderedIndex(null);
  }

  removeFunctions[listId] = onRemove || ((item, index) => {});
  if (children?.length == 0)
    return (
      <Droppable
        key={`${listId}empty`}
        id={`${listId}empty`}
        listType={listType}
        isHorizontal={isHorizontal}
        onDrop={async (isAfter) => {
          let index = 0;
          await onDrop(isAfter, index);
        }}
      >
        <div className="c1 p30 gray-bg center gap10 gray br">
          <div className="">Aucun élément</div>
          {onAddClick != null && (
            <Button type={"tertiary"} onClick={onAddClick}>
              {addButtonTitle || "Ajouter un élément"}
            </Button>
          )}
        </div>
      </Droppable>
    );

  if (isDisabled) return children || <></>;

  return (
    <>
      {children?.map((child, index) => {
        let item = items[index];
        let id = `${JSON.stringify(item)}${index}`;
        return (
          <Droppable
            key={`${listId}-${index}-${item?.id}`}
            id={id}
            listType={listType}
            onDragStart={(e) => {
              dragData = {
                id,
                draggedIndex: index,
                item,
                sourceListType: listType,
                sourceListId: listId,
              };
            }}
            isHorizontal={isHorizontal}
            additionalClass={reorderedIndex === index ? "reordered" : ""}
            onDrop={async (isAfter) => {
              await onDrop(isAfter, index);
            }}
          >
            {child}
          </Droppable>
        );
      })}
    </>
  );
}

function Droppable({
  id,
  isPending,
  listType,
  onDragStart,
  additionalClass,
  isHorizontal,
  children,
  onDrop,
}: {
  id: string;
  isPending?: boolean;
  listType: any;
  onDragStart?: (e: any) => void;
  additionalClass?: string;
  isHorizontal?: boolean;
  children: any;
  onDrop: (isAfter: boolean) => void;
}) {
  let [isDragged, setIsDragged] = useState<boolean>(false);
  let [isDraggedOver, setIsDraggedOver] = useState<boolean>(false);
  let [animateClose, setAnimateClose] = useState<boolean>(false);
  let [isAfter, setIsAfter] = useState<boolean>(false);

  function onDragOver(e: any) {
    e.preventDefault();

    if (!canDrop(e)) return;

    let x = e.clientX;
    let y = e.clientY;
    let rect = e.currentTarget.getBoundingClientRect();
    let midX = rect.left + rect.width / 2;
    let midY = rect.top + rect.height / 2;
    let newIsAfter = y > midY;
    if (isHorizontal) newIsAfter = x > midX;

    if (!isDraggedOver) {
      setIsDraggedOver(true);
    }
    stopDebounce(`dragLeave ${id}`);
    if (isAfter !== newIsAfter) setIsAfter(newIsAfter);
  }

  function onDragLeave(e: any) {
    debounce(
      `dragLeave ${id}`,
      () => {
        e.preventDefault();
        setAnimateClose(true);
        setTimeout(() => {
          setIsDraggedOver(false);
          setAnimateClose(false);
        }, 300);
      },
      300
    );
  }

  function onLocalDrop(e: any) {
    e.preventDefault();
    if (!canDrop(e)) return;

    let x = e.clientX;
    let y = e.clientY;
    let rect = e.currentTarget.getBoundingClientRect();
    let midX = rect.left + rect.width / 2;
    let midY = rect.top + rect.height / 2;
    let isAfter = x > midX && y > midY;

    onDrop(isAfter);
    onDragLeave(e);
  }

  function canDrop(e: any) {
    let data = dragData;
    if (data?.id == id) return false;
    if (data?.sourceListType != listType) return false;
    return true;
  }

  return (
    <div
      className={`droppable ${isHorizontal ? "horizontal" : ""} ${
        additionalClass || ""
      } ${isDragged ? "dragged" : ""} ${isPending ? "disabled" : ""} ${
        isDraggedOver && onDragStart == null ? "emptyDraggedOver" : ""
      }`}
      draggable={onDragStart == null ? false : true}
      onDragStart={(e) => {
        setIsDragged(true);
        onDragStart?.(e);
      }}
      onDragEnd={() => {
        setIsDragged(false);
        setIsDraggedOver(false);
      }}
      onDragOver={(e) => {
        onDragOver(e);
      }}
      onDragLeave={onDragLeave}
      onDrop={onLocalDrop}
      onTouchStart={(e) => {
        setIsDragged(true);
        onDragStart?.(e);
      }}
      onTouchEnd={() => {
        setIsDragged(false);
        setIsDraggedOver(false);
      }}
    >
      <div
        className={`dragHint  ${isHorizontal ? "horizontal" : ""} ${
          isDraggedOver && !isAfter ? "open" : ""
        } ${isDraggedOver && !isAfter && animateClose ? "animateClose" : ""}`}
      ></div>
      {children}
      <div
        className={`dragHint after   ${isHorizontal ? "horizontal" : ""} ${
          isDraggedOver && isAfter ? "open" : ""
        } ${isDraggedOver && isAfter && animateClose ? "animateClose" : ""}`}
      ></div>
    </div>
  );
}

export { ReorderableItems };
